import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface Language {
  name: string;
  code: string;
  flag?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private initialized = false;
  private readonly DEFAULT_LANG_CODE = 'br';

  public readonly AVAILABLE_LANGS: Language[] = [
    { name: 'BR', code: 'br', flag: 'flag-icon-br' },
    { name: 'PT', code: 'pt', flag: 'flag-icon-pt' },
    { name: 'EN', code: 'en', flag: 'flag-icon-us' },
    { name: 'ES', code: 'es', flag: 'flag-icon-es' }
  ];

  private currentLangSubject = new BehaviorSubject<Language>(this.AVAILABLE_LANGS[0]); // 'br'
  public currentLang$: Observable<Language> = this.currentLangSubject.asObservable();

  constructor(private translate: TranslateService) {}

  /**
   * Inicializa o serviço de idioma uma única vez
    * Usa sempre pt-BR ('br') como padrão inicial
   */
  public initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Padrão inicial sempre em pt-BR (código interno 'br')
    const selectedLang = this.AVAILABLE_LANGS.find(lang => lang.code === this.DEFAULT_LANG_CODE)
      || this.AVAILABLE_LANGS[0];

    // Inicializar uma única vez
    this.translate.use(selectedLang.code);
    this.currentLangSubject.next(selectedLang);
  }

  /**
   * Retorna o idioma atual
   */
  public getCurrentLang(): Language {
    return this.currentLangSubject.value;
  }

  /**
   * Define um novo idioma
   */
  public setLang(lang: Language | string): void {
    // Aceitar tanto objeto Language quanto string
    const langToSet = typeof lang === 'string'
      ? this.AVAILABLE_LANGS.find(l => l.code === lang) || this.AVAILABLE_LANGS[0]
      : lang;

    this.translate.use(langToSet.code);
    this.currentLangSubject.next(langToSet);
  }

  /**
   * Retorna a lista de idiomas disponíveis
   */
  public getAvailableLangs(): Language[] {
    return this.AVAILABLE_LANGS;
  }
}
